import { apiJson } from "@/shared/lib/api-client.ts";
import type {
  BackerLeagueRow,
  BackerSpecializationRow,
  CategoryFamilyMatrixCell,
  CoInvestorFinderResult,
  EcosystemSnapshotRow,
  FounderPedigreeConcentration,
  IntelligenceKpis,
  RunwayStressRow,
  ScoreFundingScatter,
  StageMixRow,
  StealthWatchlistRow,
} from "../types/index.ts";

interface ApiResponse<T> {
  data: T;
}

export async function getIntelligenceKpis(): Promise<IntelligenceKpis> {
  const payload = await apiJson<ApiResponse<IntelligenceKpis>>("/v1/admin/intelligence/kpis");
  return payload.data;
}

export async function getCategoryFamilyMatrix(): Promise<CategoryFamilyMatrixCell[]> {
  const payload = await apiJson<ApiResponse<CategoryFamilyMatrixCell[]>>(
    "/v1/admin/intelligence/category-family-matrix"
  );
  return payload.data ?? [];
}

export async function getScoreFundingScatter(): Promise<ScoreFundingScatter> {
  const payload = await apiJson<ApiResponse<ScoreFundingScatter>>(
    "/v1/admin/intelligence/score-funding-scatter"
  );
  return payload.data;
}

export async function getBackerLeague(limit = 20): Promise<BackerLeagueRow[]> {
  const payload = await apiJson<ApiResponse<BackerLeagueRow[]>>(
    `/v1/admin/intelligence/backer-league?limit=${limit}`
  );
  return payload.data ?? [];
}

export async function getBackerSpecialization(limit = 30): Promise<BackerSpecializationRow[]> {
  const payload = await apiJson<ApiResponse<BackerSpecializationRow[]>>(
    `/v1/admin/intelligence/backer-specialization?limit=${limit}`
  );
  return payload.data ?? [];
}

export async function getCoInvestorComparables(
  projectId: string,
  minShared = 3
): Promise<CoInvestorFinderResult> {
  const payload = await apiJson<ApiResponse<CoInvestorFinderResult>>(
    `/v1/admin/intelligence/co-investors/${encodeURIComponent(projectId)}?minShared=${minShared}`
  );
  return payload.data;
}

export async function getRunwayStressList(limit = 50): Promise<RunwayStressRow[]> {
  const payload = await apiJson<ApiResponse<RunwayStressRow[]>>(
    `/v1/admin/intelligence/runway-stress?limit=${limit}`
  );
  return payload.data ?? [];
}

export async function getStealthWatchlist(limit = 50): Promise<StealthWatchlistRow[]> {
  const payload = await apiJson<ApiResponse<StealthWatchlistRow[]>>(
    `/v1/admin/intelligence/stealth-watchlist?limit=${limit}`
  );
  return payload.data ?? [];
}

export async function getFounderPedigree(): Promise<FounderPedigreeConcentration> {
  const payload = await apiJson<ApiResponse<FounderPedigreeConcentration>>(
    "/v1/admin/intelligence/founder-pedigree"
  );
  return (
    payload.data ?? {
      byScoreBand: [],
      topProjects: {
        topN: 50,
        totalProjects: 0,
        withPedigree: 0,
        tier1Count: 0,
        tier1Pct: 0,
      },
    }
  );
}

export async function getEcosystemSnapshot(minSample = 5): Promise<EcosystemSnapshotRow[]> {
  const payload = await apiJson<ApiResponse<EcosystemSnapshotRow[]>>(
    `/v1/admin/intelligence/ecosystem-snapshot?minSample=${minSample}`
  );
  return payload.data ?? [];
}

export async function getStageMix(): Promise<StageMixRow[]> {
  const payload = await apiJson<ApiResponse<StageMixRow[]>>("/v1/admin/intelligence/stage-mix");
  return payload.data ?? [];
}
